import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../api/axiosInstance";
import { Container, Spinner } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { Viewer } from "@toast-ui/react-editor";
import "@toast-ui/editor/dist/toastui-editor-viewer.css";

const DEFAULT_WEATHER_IMG = "https://images.unsplash.com/photo-1504608524841-42fe6f032b4b?w=800&auto=format&fit=crop&q=60";

// --- 생동감 넘치는 커스텀 애니메이션 SVG 컴포넌트 ---
const AnimatedSunny = () => (
    <svg className="weather-animated-icon sunny" viewBox="0 0 64 64" width="28" height="28" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <defs>
            <linearGradient id="sun-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fbc531" />
                <stop offset="100%" stopColor="#e1b12c" />
            </linearGradient>
        </defs>
        <g transform="translate(32,32)">
            <g className="sun-spin">
                <circle r="12" fill="url(#sun-grad)" className="sun-core" />
                {Array.from({ length: 8 }).map((_, i) => {
                    const angle = i * 45;
                    return (
                        <line
                            key={i}
                            x1="0" y1="-17" x2="0" y2="-23"
                            stroke="#fbc531" strokeWidth="3" strokeLinecap="round"
                            transform={`rotate(${angle})`}
                            className="sun-ray"
                            style={{ animationDelay: `${i * 0.15}s` }}
                        />
                    );
                })}
            </g>
        </g>
    </svg>
);

const AnimatedPartlyCloudy = () => (
    <svg className="weather-animated-icon partly-cloudy" viewBox="0 0 64 64" width="28" height="28" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <defs>
            <linearGradient id="cloud-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#dcdde1" />
            </linearGradient>
            <linearGradient id="sun-grad-2" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fbc531" />
                <stop offset="100%" stopColor="#e1b12c" />
            </linearGradient>
        </defs>
        <g transform="translate(24, 24)" className="sun-spin-slow">
            <circle r="8" fill="url(#sun-grad-2)" />
            {Array.from({ length: 8 }).map((_, i) => (
                <line
                    key={i}
                    x1="0" y1="-12" x2="0" y2="-16"
                    stroke="#fbc531" strokeWidth="2" strokeLinecap="round"
                    transform={`rotate(${i * 45})`}
                />
            ))}
        </g>
        <path
            d="M46 36a8 8 0 0 1-5 7.4A12 12 0 0 1 20 40a9 9 0 0 1 1.7-8.3A10 10 0 0 1 36 28a8 8 0 0 1 10 8z"
            fill="url(#cloud-grad)"
            className="cloud-drift"
        />
    </svg>
);

const AnimatedCloudy = () => (
    <svg className="weather-animated-icon cloudy" viewBox="0 0 64 64" width="28" height="28" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <defs>
            <linearGradient id="cloud-dark" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#dcdde1" />
                <stop offset="100%" stopColor="#7f8c8d" />
            </linearGradient>
            <linearGradient id="cloud-light" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#bdc3c7" />
            </linearGradient>
        </defs>
        <path
            d="M38 30a7 7 0 0 1-4.4 6.5A10 10 0 0 1 15 33a8 8 0 0 1 1.5-7.3A9 9 0 0 1 29 23a7 7 0 0 1 9 7z"
            fill="url(#cloud-dark)"
            className="cloud-drift-back"
        />
        <path
            d="M48 38a8 8 0 0 1-5 7.4A12 12 0 0 1 22 42a9 9 0 0 1 1.7-8.3A10 10 0 0 1 38 30a8 8 0 0 1 10 8z"
            fill="url(#cloud-light)"
            className="cloud-drift-front"
        />
    </svg>
);

const AnimatedRainy = () => (
    <svg className="weather-animated-icon rainy" viewBox="0 0 64 64" width="28" height="28" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <defs>
            <linearGradient id="cloud-rain" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#bdc3c7" />
                <stop offset="100%" stopColor="#7f8c8d" />
            </linearGradient>
        </defs>
        <path
            d="M46 32a8 8 0 0 1-5 7.4A12 12 0 0 1 20 36a9 9 0 0 1 1.7-8.3A10 10 0 0 1 36 24a8 8 0 0 1 10 8z"
            fill="url(#cloud-rain)"
            className="cloud-drift"
        />
        <line x1="24" y1="42" x2="20" y2="52" stroke="#3498db" strokeWidth="2.5" strokeLinecap="round" className="rain-drop drop-1" />
        <line x1="32" y1="44" x2="28" y2="54" stroke="#3498db" strokeWidth="2.5" strokeLinecap="round" className="rain-drop drop-2" />
        <line x1="40" y1="42" x2="36" y2="52" stroke="#3498db" strokeWidth="2.5" strokeLinecap="round" className="rain-drop drop-3" />
    </svg>
);

const AnimatedSnowy = () => (
    <svg className="weather-animated-icon snowy" viewBox="0 0 64 64" width="28" height="28" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <defs>
            <linearGradient id="cloud-snow" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#bdc3c7" />
            </linearGradient>
        </defs>
        <path
            d="M46 32a8 8 0 0 1-5 7.4A12 12 0 0 1 20 36a9 9 0 0 1 1.7-8.3A10 10 0 0 1 36 24a8 8 0 0 1 10 8z"
            fill="url(#cloud-snow)"
            className="cloud-drift"
        />
        <g stroke="#74b9ff" strokeWidth="2.5" strokeLinecap="round">
            <g transform="translate(24, 44)">
                <g className="snow-flake flake-1">
                    <line x1="-3" y1="0" x2="3" y2="0" />
                    <line x1="0" y1="-3" x2="0" y2="3" />
                </g>
            </g>
            <g transform="translate(32, 46)">
                <g className="snow-flake flake-2">
                    <line x1="-3" y1="0" x2="3" y2="0" />
                    <line x1="0" y1="-3" x2="0" y2="3" />
                </g>
            </g>
            <g transform="translate(40, 44)">
                <g className="snow-flake flake-3">
                    <line x1="-3" y1="0" x2="3" y2="0" />
                    <line x1="0" y1="-3" x2="0" y2="3" />
                </g>
            </g>
        </g>
    </svg>
);

const AnimatedWindy = () => (
    <svg className="weather-animated-icon windy" viewBox="0 0 64 64" width="28" height="28" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <path
            d="M12 24h24a4 4 0 0 1 0 8H32"
            fill="none" stroke="#2bcbba" strokeWidth="3" strokeLinecap="round"
            className="wind-line line-1"
        />
        <path
            d="M8 36h32a4 4 0 0 0 0-8H36"
            fill="none" stroke="#2bcbba" strokeWidth="3" strokeLinecap="round"
            className="wind-line line-2"
        />
        <path
            d="M16 44h16"
            fill="none" stroke="#2bcbba" strokeWidth="3" strokeLinecap="round"
            className="wind-line line-3"
        />
    </svg>
);

const AnimatedThunderstorm = () => (
    <svg className="weather-animated-icon thunderstorm" viewBox="0 0 64 64" width="28" height="28" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <defs>
            <linearGradient id="cloud-thunder" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#7f8c8d" />
                <stop offset="100%" stopColor="#2c3e50" />
            </linearGradient>
        </defs>
        <path
            d="M46 32a8 8 0 0 1-5 7.4A12 12 0 0 1 20 36a9 9 0 0 1 1.7-8.3A10 10 0 0 1 36 24a8 8 0 0 1 10 8z"
            fill="url(#cloud-thunder)"
            className="cloud-drift"
        />
        <polygon
            points="32,36 24,46 30,46 26,54 38,42 32,42"
            fill="#f1c40f"
            className="lightning-bolt"
        />
    </svg>
);

const AnimatedHumidity = () => (
    <svg className="weather-animated-icon humidity" viewBox="0 0 64 64" width="22" height="22" style={{ marginRight: '1px', display: 'inline-block', verticalAlign: 'middle' }}>
        <defs>
            <linearGradient id="drop-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#74b9ff" />
                <stop offset="100%" stopColor="#0984e3" />
            </linearGradient>
        </defs>
        <path
            d="M32 12 C32 12, 16 32, 16 42 A 16 16 0 0 0 48 42 C 48 32, 32 12, 32 12 Z"
            fill="url(#drop-grad)"
            className="humidity-drop"
        />
    </svg>
);



const getWeatherIcon = (condition) => {
    switch (condition) {
        case "Sunny": return <AnimatedSunny />;
        case "PartlyCloudy": return <AnimatedPartlyCloudy />;
        case "Cloudy": return <AnimatedCloudy />;
        case "Rainy": return <AnimatedRainy />;
        case "Snowy": return <AnimatedSnowy />;
        case "Windy": return <AnimatedWindy />;
        case "Thunderstorm": return <AnimatedThunderstorm />;
        default: return null;
    }
};

const getWeatherName = (condition) => {
    switch (condition) {
        case "Sunny": return "맑음";
        case "PartlyCloudy": return "구름조금";
        case "Cloudy": return "흐림";
        case "Rainy": return "비";
        case "Snowy": return "눈";
        case "Windy": return "바람";
        case "Thunderstorm": return "번개";
        default: return "";
    }
};

const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
    const day = weekdays[date.getDay()];
    return `${y}년 ${m}월 ${d}일 (${day})`;
};

const formatTmFc = (tmFc) => {
    if (!tmFc) return "";
    const str = tmFc.toString();
    if (str.length === 12) {
        // YYYYMMDDHHMM
        return `${str.substring(0, 4)}년 ${str.substring(4, 6)}월 ${str.substring(6, 8)}일 ${str.substring(8, 10)}시 ${str.substring(10, 12)}분`;
    }
    if (str.length === 8) {
        // YYYYMMDD
        return `${str.substring(0, 4)}년 ${str.substring(4, 6)}월 ${str.substring(6, 8)}일`;
    }
    return str;
};

const replaceLiteralNewlines = (text) => {
    if (!text) return "";
    return text.toString().replace(/\\n/g, "\n");
};

// 미세먼지 등급별 맞춤 컬러 획득 함수
const getGradeColor = (grade) => {
    switch (grade) {
        case "좋음":
            return { bg: "rgba(46, 204, 113, 0.12)", text: "#27ae60", border: "rgba(46, 204, 113, 0.3)" };
        case "보통":
            return { bg: "rgba(52, 152, 219, 0.12)", text: "#2980b9", border: "rgba(52, 152, 219, 0.3)" };
        case "나쁨":
            return { bg: "rgba(230, 126, 34, 0.12)", text: "#d35400", border: "rgba(230, 126, 34, 0.3)" };
        case "매우나쁨":
            return { bg: "rgba(231, 76, 60, 0.12)", text: "#c0392b", border: "rgba(231, 76, 60, 0.3)" };
        default:
            return { bg: "rgba(127, 140, 141, 0.12)", text: "#7f8c8d", border: "rgba(127, 140, 141, 0.3)" };
    }
};

// 미세먼지 예보 등급 파싱 함수
const parseGrades = (gradeStr) => {
    if (!gradeStr) return [];
    return gradeStr.split(',').map(item => {
        const parts = item.split(':');
        if (parts.length < 2) return null;
        const region = parts[0].trim();
        const grade = parts[1].trim();
        return { region, grade };
    }).filter(item => item !== null && item.region && item.grade);
};

export const WeatherBoard = () => {
    // Redux State
    const fineDust = useSelector((state) => state.fineDust);
    const privateData = useSelector((state) => state.privateData);

    // Current Weather State
    const [currentWeather, setCurrentWeather] = useState(null);
    const [weatherLoading, setWeatherLoading] = useState(false);
    const [weatherError, setWeatherError] = useState(false);

    // Fine Dust State
    const [pm10State, setPm10State] = useState(null);
    const [dustLoading, setDustLoading] = useState(false);
    const [dustError, setDustError] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [imageUrls, setImageUrls] = useState([]);

    // Gov API 5종 State
    const [typhoonState, setTyphoonState] = useState({ loading: false, error: false, data: null });
    const [warningState, setWarningState] = useState({ loading: false, error: false, data: null });
    const [infoState, setInfoState] = useState({ loading: false, error: false, data: null });
    const [breakState, setBreakState] = useState({ loading: false, error: false, data: null });
    const [advisoryState, setAdvisoryState] = useState({ loading: false, error: false, data: null });

    // Active Tab State
    const [activeTab, setActiveTab] = useState("weather-warning");
    const [refreshing, setRefreshing] = useState(false);

    // Fetch Current Weather
    const fetchCurrentWeather = useCallback(async () => {
        setWeatherLoading(true);
        setWeatherError(false);
        try {
            const response = await axiosInstance.get("posts?board_id=5&page=1");
            const apiData = response.data.data;
            if (apiData && apiData.postList && apiData.postList.length > 0) {
                setCurrentWeather(apiData.postList[0]);
            } else {
                setCurrentWeather(null);
            }
        } catch (error) {
            console.error("Error fetching weather post:", error);
            setWeatherError(true);
        } finally {
            setWeatherLoading(false);
        }
    }, []);

    // Fetch Fine Dust
    const fetchFineDust = useCallback(async () => {
        if (!fineDust?.fineDustUrl || !privateData?.serviceKey) return;
        setDustLoading(true);
        setDustError(false);
        try {
            const today = new Date();
            const year = today.getFullYear();
            const month = (today.getMonth() + 1).toString().padStart(2, '0');
            const day = today.getDate().toString().padStart(2, '0');
            const response = await axiosInstance.get(
                `${fineDust.fineDustUrl}?serviceKey=${privateData.serviceKey}&returnType=json&numOfRows=100&pageNo=1&searchDate=${year}-${month}-${day}&InformCode=PM10`,
                {
                    headers: { 'Accept': 'application/json' }
                }
            );
            const items = response.data?.response?.body?.items;
            if (!items) {
                setPm10State(null);
                setDustError(true);
                return;
            }

            const list = Array.isArray(items) ? items : [items];
            let found = null;
            for (let obj of list) {
                if (obj.informCode === 'PM10' && obj.imageUrl1 !== null) {
                    found = obj;
                    break;
                }
            }
            if (found) {
                setPm10State(found);
            } else {
                setPm10State(null);
                setDustError(true);
            }
        } catch (error) {
            console.error("Error fetching fine dust:", error);
            setDustError(true);
        } finally {
            setDustLoading(false);
        }
    }, [fineDust, privateData]);

    // Fetch Gov Data via webhook
    const fetchGovData = useCallback(async (serviceType, setState) => {
        setState(prev => ({ ...prev, loading: true, error: false }));
        try {
            const response = await axiosInstance.post("gov-data", { serviceType });
            const items = response.data?.response?.body?.items?.item;

            let list = [];
            if (items) {
                if (Array.isArray(items)) {
                    list = items;
                } else {
                    list = [items];
                }
            }
            setState({ loading: false, error: false, data: list });
        } catch (error) {
            console.error(`Error fetching gov-data for ${serviceType}:`, error);
            setState({ loading: false, error: true, data: null });
        }
    }, []);

    // Global Load / Refresh
    const handleRefreshAll = async () => {
        setRefreshing(true);
        await Promise.all([
            fetchCurrentWeather(),
            fetchFineDust(),
            fetchGovData("weather-warning", setWarningState),
            fetchGovData("weather-advisory", setAdvisoryState),
            fetchGovData("weather-break", setBreakState),
            fetchGovData("weather-info", setInfoState),
            fetchGovData("typhoon", setTyphoonState)
        ]);
        setRefreshing(false);
    };

    useEffect(() => {
        fetchCurrentWeather();
        fetchFineDust();
        fetchGovData("weather-warning", setWarningState);
        fetchGovData("weather-advisory", setAdvisoryState);
        fetchGovData("weather-break", setBreakState);
        fetchGovData("weather-info", setInfoState);
        fetchGovData("typhoon", setTyphoonState);
    }, [fetchCurrentWeather, fetchFineDust, fetchGovData]);

    // Fine Dust loop map timeline
    useEffect(() => {
        if (pm10State) {
            setImageUrls([
                pm10State.imageUrl1,
                pm10State.imageUrl2,
                pm10State.imageUrl3
            ].filter(url => url !== null));
        }
    }, [pm10State]);

    useEffect(() => {
        if (imageUrls.length === 0) return;
        const interval = setInterval(() => {
            setCurrentIndex(prevIndex => (prevIndex + 1) % imageUrls.length);
        }, 500);
        return () => clearInterval(interval);
    }, [imageUrls]);

    // Extract image and clean content from post
    const parsePostContent = (content) => {
        if (!content) return { imageUrl: DEFAULT_WEATHER_IMG, cleanContent: "" };
        const imgRegex = /!\[.*?\]\((.*?)\)/;
        const match = content.match(imgRegex);
        const imageUrl = match ? match[1] : DEFAULT_WEATHER_IMG;
        const cleanContent = content.replace(imgRegex, "").trim();
        return { imageUrl, cleanContent };
    };

    const getTabState = (tabId) => {
        switch (tabId) {
            case "weather-warning": return warningState;
            case "weather-advisory": return advisoryState;
            case "weather-break": return breakState;
            case "weather-info": return infoState;
            case "typhoon": return typhoonState;
            default: return { loading: false, error: false, data: null };
        }
    };

    const handleRetry = (tabId) => {
        switch (tabId) {
            case "weather-warning": fetchGovData("weather-warning", setWarningState); break;
            case "weather-advisory": fetchGovData("weather-advisory", setAdvisoryState); break;
            case "weather-break": fetchGovData("weather-break", setBreakState); break;
            case "weather-info": fetchGovData("weather-info", setInfoState); break;
            case "typhoon": fetchGovData("typhoon", setTyphoonState); break;
            default: break;
        }
    };

    const tabs = [
        { id: "weather-warning", label: "기상특보", icon: "🚨" },
        { id: "weather-advisory", label: "예비특보", icon: "⚠️" },
        { id: "weather-break", label: "기상속보", icon: "⚡" },
        { id: "weather-info", label: "기상정보", icon: "ℹ️" },
        { id: "typhoon", label: "태풍정보", icon: "🌀" }
    ];

    const renderTabContent = (tabId) => {
        const state = getTabState(tabId);

        if (state.loading) {
            return (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" className="mb-2" />
                    <p className="text-muted small fw-bold">기상청에서 데이터를 불러오고 있습니다...</p>
                </div>
            );
        }

        if (state.error) {
            return (
                <div className="text-center py-5 text-danger">
                    <div className="fs-3 mb-2">⚠️</div>
                    <p className="fw-bold mb-1">실시간 데이터를 로드하는 도중 오류가 발생했습니다.</p>
                    <p className="text-muted small mb-3">공공데이터 응답 속도 지연 또는 일시적 네트워크 장애일 수 있습니다.</p>
                    <button className="btn btn-sm btn-outline-danger px-3 rounded-pill" onClick={() => handleRetry(tabId)}>
                        다시 시도
                    </button>
                </div>
            );
        }

        if (!state.data || state.data.length === 0) {
            return (
                <div className="text-center py-5 text-muted">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#2ecc71" strokeWidth="2" className="mb-3" style={{ display: 'inline-block' }}>
                        <circle cx="12" cy="12" r="10" />
                        <path d="M8 12l3 3 5-5" />
                    </svg>
                    <p className="fw-bold text-success mb-1" style={{ fontSize: '1.1rem' }}>현재 활성화된 특보 정보가 없습니다.</p>
                    <p className="small text-secondary">기상청에서 발표한 활성 데이터가 없는 평온한 상태입니다.</p>
                </div>
            );
        }

        return (
            <div className="tab-result-content">
                {state.data.map((item, idx) => {
                    switch (tabId) {
                        case "weather-warning":
                            return (
                                <div key={idx} className="alert-item-container mb-4">
                                    <div className="alert-item-header d-flex justify-content-between align-items-center mb-3">
                                        <span className="badge bg-danger px-3 py-2 rounded-pill">기상특보 발표</span>
                                        <span className="text-muted small fw-bold">⏱️ 발표시각: {formatTmFc(item.tmFc)}</span>
                                    </div>
                                    <h4 className="alert-item-title text-dark fw-bold mb-3">{item.t1}</h4>

                                    {item.t6 && (
                                        <div className="alert-section-box active-warnings mb-3">
                                            <h5 className="section-title text-danger fw-bold">⚠️ 현재 발령 중인 특보 현황</h5>
                                            <div className="section-content text-dark pre-wrap">{replaceLiteralNewlines(item.t6)}</div>
                                        </div>
                                    )}

                                    {item.t2 && (
                                        <div className="alert-section-box detail-regions mb-3">
                                            <h5 className="section-title text-primary fw-bold">🗺️ 특보 상세 지역 및 발표 세부 정보</h5>
                                            <div className="section-content text-dark pre-wrap">{replaceLiteralNewlines(item.t2)}</div>
                                        </div>
                                    )}

                                    {item.t3 && (
                                        <div className="alert-section-box execution-times mb-3">
                                            <h5 className="section-title text-secondary fw-bold">📅 해제 및 발효 시각 정보</h5>
                                            <div className="section-content text-dark pre-wrap">{replaceLiteralNewlines(item.t3)}</div>
                                        </div>
                                    )}

                                    {item.t7 && (
                                        <div className="alert-section-box preliminary-warnings mb-3">
                                            <h5 className="section-title text-warning fw-bold">💡 예비 특보 현황</h5>
                                            <div className="section-content text-dark pre-wrap">{replaceLiteralNewlines(item.t7)}</div>
                                        </div>
                                    )}

                                    {item.other && (
                                        <div className="alert-section-box action-guidelines mb-3">
                                            <h5 className="section-title text-success fw-bold">🛡️ 행동요령 및 참고 사항</h5>
                                            <div className="section-content text-secondary pre-wrap">{replaceLiteralNewlines(item.other)}</div>
                                        </div>
                                    )}
                                </div>
                            );
                        case "weather-advisory":
                            return (
                                <div key={idx} className="alert-item-container mb-4">
                                    <div className="alert-item-header d-flex justify-content-between align-items-center mb-3">
                                        <span className="badge bg-warning text-dark px-3 py-2 rounded-pill">예비특보 현황</span>
                                        <span className="text-muted small fw-bold">⏱️ 발표시각: {formatTmFc(item.tmFc)}</span>
                                    </div>
                                    {item.pwn && (
                                        <div className="alert-section-box preliminary-warnings mb-3">
                                            <h5 className="section-title text-warning fw-bold">⚠️ 예비특보 발표 상황</h5>
                                            <div className="section-content text-dark pre-wrap">{replaceLiteralNewlines(item.pwn)}</div>
                                        </div>
                                    )}
                                    {item.rem && (
                                        <div className="alert-section-box mb-3">
                                            <h5 className="section-title text-dark fw-bold">📝 유의 사항 및 세부 분석</h5>
                                            <div className="section-content text-secondary pre-wrap">{replaceLiteralNewlines(item.rem)}</div>
                                        </div>
                                    )}
                                </div>
                            );
                        case "weather-break":
                            return (
                                <div key={idx} className="alert-item-container mb-4">
                                    <div className="alert-item-header d-flex justify-content-between align-items-center mb-3">
                                        <span className="badge bg-primary px-3 py-2 rounded-pill">기상속보</span>
                                        <span className="text-muted small fw-bold">⏱️ 발표시각: {formatTmFc(item.tmFc)}</span>
                                    </div>
                                    {item.ann && (
                                        <div className="alert-section-box mb-3" style={{ borderLeftColor: "#2980b9" }}>
                                            <h5 className="section-title text-primary fw-bold">⚡ 실시간 기상 속보 브리핑</h5>
                                            <div className="section-content text-dark pre-wrap">{replaceLiteralNewlines(item.ann)}</div>
                                        </div>
                                    )}
                                </div>
                            );
                        case "weather-info":
                            return (
                                <div key={idx} className="alert-item-container mb-4">
                                    <div className="alert-item-header d-flex justify-content-between align-items-center mb-3">
                                        <span className="badge bg-info text-dark px-3 py-2 rounded-pill">기상 정보</span>
                                        <span className="text-muted small fw-bold">⏱️ 발표시각: {formatTmFc(item.tmFc)}</span>
                                    </div>
                                    {item.t1 && (
                                        <div className="alert-section-box mb-3" style={{ borderLeftColor: "#3498db" }}>
                                            <h5 className="section-title text-info fw-bold">ℹ️ 기상 현황 및 전망 상세</h5>
                                            <div className="section-content text-dark pre-wrap">{replaceLiteralNewlines(item.t1)}</div>
                                        </div>
                                    )}
                                </div>
                            );
                        case "typhoon":
                            return (
                                <div key={idx} className="alert-item-container mb-4">
                                    <div className="alert-item-header d-flex justify-content-between align-items-center mb-3">
                                        <span className="badge bg-dark text-white px-3 py-2 rounded-pill">🌀 태풍 정보</span>
                                        <span className="text-muted small fw-bold">⏱️ 발표시각: {formatTmFc(item.tmFc)}</span>
                                    </div>
                                    <h4 className="text-primary fw-bold mb-3">
                                        제{item.typSeq}호 태풍 "{item.typName}" ({item.typEn})
                                    </h4>

                                    <div className="typhoon-grid-layout">
                                        <div className="typhoon-specs-panel">
                                            <table className="table table-bordered typhoon-specs-table">
                                                <tbody>
                                                    <tr>
                                                        <th>현재 위치</th>
                                                        <td>{item.typLoc}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>위도/경도</th>
                                                        <td>북위 {item.typLat}° / 동경 {item.typLon}°</td>
                                                    </tr>
                                                    <tr>
                                                        <th>중심 기압</th>
                                                        <td>{item.typPs} hPa</td>
                                                    </tr>
                                                    <tr>
                                                        <th>최대 풍속</th>
                                                        <td>{item.typWs} m/s (시속 {Math.round(item.typWs * 3.6)} km/h)</td>
                                                    </tr>
                                                    <tr>
                                                        <th>진행 방향/속도</th>
                                                        <td>{item.typDir} 방향 / 시속 {item.typSp} km/h</td>
                                                    </tr>
                                                </tbody>
                                            </table>

                                            {item.other && (
                                                <div className="typhoon-desc mb-3">
                                                    <h5 className="section-title text-dark fw-bold">🌀 태풍 특징 및 명칭 어원</h5>
                                                    <p className="text-secondary pre-wrap mb-0" style={{ fontSize: '0.9rem' }}>{replaceLiteralNewlines(item.other)}</p>
                                                </div>
                                            )}

                                            {item.rem && (
                                                <div className="typhoon-rem text-muted small pre-wrap border-top pt-2">
                                                    <strong>안내 및 주의사항:</strong> <br />
                                                    {item.rem.split('|').filter(r => r.trim()).map((r, i) => (
                                                        <div key={i}>• {replaceLiteralNewlines(r)}</div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {item.img && (
                                            <div className="typhoon-image-panel text-center">
                                                <h5 className="section-title text-secondary fw-bold mb-2">🗺️ 태풍 예상 이동 경로도</h5>
                                                <div className="typhoon-img-box">
                                                    <img src={item.img} className="typhoon-map-img img-fluid rounded border shadow-sm" alt={`태풍 ${item.typName} 경로`} onError={(e) => e.target.style.display = 'none'} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        default:
                            return null;
                    }
                })}
            </div>
        );
    };

    return (
        <>
            <style>
                {`
                    .weather-dashboard {
                        max-width: 1200px;
                        margin: 0 auto;
                        padding: 2rem 1.5rem;
                        font-family: 'Noto Sans KR', sans-serif;
                        color: #2c3e50;
                    }

                    /* 상단 기상 정보 배너 */
                    .weather-header {
                        background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
                        border-radius: 24px;
                        padding: 2.5rem 2rem;
                        color: white;
                        box-shadow: 0 12px 30px rgba(30, 60, 114, 0.15);
                        margin-bottom: 2rem;
                        position: relative;
                        overflow: hidden;
                    }
                    .weather-header::after {
                        content: '';
                        position: absolute;
                        top: -50%;
                        right: -20%;
                        width: 60%;
                        height: 200%;
                        background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 70%);
                        transform: rotate(-15deg);
                        pointer-events: none;
                    }
                    .weather-header-title {
                        font-weight: 800;
                        font-size: 2rem;
                        letter-spacing: -0.5px;
                        margin-bottom: 0.5rem;
                    }
                    .weather-header-subtitle {
                        font-size: 1rem;
                        opacity: 0.85;
                        margin-bottom: 0;
                    }
                    .weather-header-meta {
                        margin-top: 20px;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        flex-wrap: wrap;
                        gap: 15px;
                        border-top: 1px solid rgba(255,255,255,0.15);
                        padding-top: 15px;
                    }
                    .update-badge {
                        background: rgba(255,255,255,0.15);
                        backdrop-filter: blur(10px);
                        border: 1px solid rgba(255,255,255,0.2);
                        padding: 6px 16px;
                        border-radius: 30px;
                        font-size: 0.85rem;
                        font-weight: 600;
                    }
                    .btn-refresh {
                        background: white;
                        color: #1e3c72;
                        border: none;
                        padding: 8px 18px;
                        border-radius: 30px;
                        font-weight: 700;
                        font-size: 0.85rem;
                        transition: all 0.2s ease;
                        box-shadow: 0 4px 10px rgba(0,0,0,0.1);
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        cursor: pointer;
                    }
                    .btn-refresh:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 6px 15px rgba(0,0,0,0.15);
                        background: #f8f9fa;
                    }
                    .spin-icon {
                        animation: spin 1s linear infinite;
                    }
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }

                    /* 대시보드 2열 레이아웃 */
                    .weather-top-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 24px;
                        margin-bottom: 24px;
                    }
                    @media (max-width: 991.98px) {
                        .weather-top-grid {
                            grid-template-columns: 1fr;
                        }
                    }

                    /* 공통 카드 스타일 */
                    .weather-card {
                        background: rgba(255, 255, 255, 0.95);
                        border: 1px solid rgba(222, 226, 230, 0.6);
                        border-radius: 20px;
                        padding: 24px;
                        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.02);
                        display: flex;
                        flex-direction: column;
                        min-height: 520px;
                    }
                    .weather-card-header {
                        border-bottom: 1px solid #f1f3f5;
                        padding-bottom: 14px;
                        margin-bottom: 18px;
                    }
                    .weather-card-title {
                        font-size: 1.25rem;
                        font-weight: 800;
                        margin: 0;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        color: #2c3e50;
                    }

                    /* 현재 기상 상태 영역 */
                    .weather-current-detail {
                        display: flex;
                        flex-direction: column;
                        gap: 16px;
                        flex-grow: 1;
                    }
                    .weather-main-status {
                        background: linear-gradient(135deg, rgba(224, 236, 255, 0.4) 0%, rgba(240, 248, 255, 0.4) 100%);
                        border: 1px solid rgba(90, 144, 230, 0.15);
                        border-radius: 16px;
                        padding: 20px;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 12px;
                    }
                    .weather-status-left {
                        display: flex;
                        flex-direction: column;
                    }
                    .weather-status-label {
                        font-size: 0.8rem;
                        font-weight: 700;
                        color: #4a69bd;
                        margin-bottom: 2px;
                        text-transform: uppercase;
                    }
                    .weather-status-name {
                        font-size: 1.6rem;
                        font-weight: 800;
                        color: #1e3799;
                        display: flex;
                        align-items: center;
                        gap: 6px;
                    }
                    .weather-main-temp {
                        display: flex;
                        flex-direction: column;
                        align-items: flex-end;
                    }
                    .weather-temp-label {
                        font-size: 0.75rem;
                        color: #7f8c8d;
                        font-weight: 700;
                        margin-bottom: 2px;
                    }
                    .weather-temp-range {
                        font-size: 1.4rem;
                        font-weight: 800;
                        color: #2c3e50;
                    }
                    .weather-temp-low {
                        color: #0984e3;
                    }
                    .weather-temp-high {
                        color: #d63031;
                    }

                    /* 날씨 통계 스트립 */
                    .weather-stats-strip {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 12px;
                    }
                    .weather-stat-box {
                        background: #f8f9fa;
                        border: 1px solid #e9ecef;
                        border-radius: 12px;
                        padding: 12px;
                        display: flex;
                        align-items: center;
                        gap: 10px;
                    }
                    .weather-stat-info {
                        display: flex;
                        flex-direction: column;
                    }
                    .weather-stat-label {
                        font-size: 0.75rem;
                        color: #7f8c8d;
                        font-weight: 600;
                    }
                    .weather-stat-value {
                        font-size: 0.9rem;
                        font-weight: 700;
                        color: #2c3e50;
                    }

                    /* 날씨 기사 */
                    .weather-post-wrapper {
                        flex-grow: 1;
                        border-top: 1px dashed #e9ecef;
                        padding-top: 15px;
                        display: flex;
                        flex-direction: column;
                    }
                    .weather-post-title {
                        font-size: 1.05rem;
                        font-weight: 800;
                        color: #2c3e50;
                        margin-bottom: 4px;
                    }
                    .weather-post-date {
                        font-size: 0.8rem;
                        color: #7f8c8d;
                        margin-bottom: 12px;
                        font-weight: 600;
                    }
                    .weather-post-body {
                        flex-grow: 1;
                    }
                    .weather-post-body .toastui-editor-contents {
                        font-size: 0.9rem;
                        line-height: 1.5;
                        color: #495057;
                    }
                    .weather-post-body .toastui-editor-contents img {
                        display: none !important; /* 이미지 중복 노출 방지 */
                    }

                    /* 미세먼지 디자인 */
                    .finedust-detail {
                        display: flex;
                        flex-direction: column;
                        gap: 15px;
                        flex-grow: 1;
                    }
                    .finedust-forecast-area {
                        display: grid;
                        grid-template-columns: 160px 1fr;
                        gap: 16px;
                        align-items: start;
                    }
                    @media (max-width: 575px) {
                        .finedust-forecast-area {
                            grid-template-columns: 1fr;
                        }
                    }
                    .finedust-map-box {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                    }
                    .finedust-map-img-container {
                        width: 100%;
                        aspect-ratio: 3/4;
                        border-radius: 12px;
                        overflow: hidden;
                        background: #f8f9fa;
                        border: 1px solid #e9ecef;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        box-shadow: inset 0 0 8px rgba(0,0,0,0.02);
                    }
                    .finedust-map-img {
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                    }
                    .finedust-map-dots {
                        display: flex;
                        gap: 6px;
                        margin-top: 10px;
                    }
                    .finedust-dot {
                        width: 6px;
                        height: 6px;
                        border-radius: 50%;
                        background-color: #dfe6e9;
                        transition: all 0.3s ease;
                    }
                    .finedust-dot.active {
                        background-color: #747d8c;
                        transform: scale(1.3);
                    }
                    .finedust-text-info {
                        display: flex;
                        flex-direction: column;
                        gap: 10px;
                    }
                    .finedust-info-section {
                        background: #f8f9fa;
                        border: 1px solid #e9ecef;
                        border-radius: 12px;
                        padding: 12px;
                    }
                    .finedust-info-section-title {
                        font-size: 0.8rem;
                        font-weight: 700;
                        color: #57606f;
                        margin-bottom: 4px;
                        display: flex;
                        align-items: center;
                        gap: 4px;
                    }
                    .finedust-info-section-text {
                        font-size: 0.85rem;
                        line-height: 1.45;
                        color: #2f3542;
                        margin-bottom: 0;
                    }
                    .finedust-grades-panel {
                        border-top: 1px dashed #e9ecef;
                        padding-top: 12px;
                    }
                    .finedust-grades-title {
                        font-size: 0.9rem;
                        font-weight: 800;
                        color: #2c3e50;
                        margin-bottom: 8px;
                    }
                    .finedust-grades-grid {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 6px;
                    }
                    .finedust-grade-chip {
                        padding: 4px 8px;
                        border-radius: 20px;
                        font-size: 0.75rem;
                        font-weight: 700;
                        border: 1px solid transparent;
                    }

                    /* 하단 특보 대시보드 */
                    .alerts-card {
                        background: white;
                        border: 1px solid rgba(222, 226, 230, 0.6);
                        border-radius: 20px;
                        padding: 24px;
                        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.02);
                    }
                    .alerts-card-header {
                        border-bottom: 1px solid #f1f3f5;
                        padding-bottom: 16px;
                        margin-bottom: 20px;
                    }
                    .alerts-card-title {
                        font-size: 1.3rem;
                        font-weight: 800;
                        color: #1e3c72;
                        margin: 0;
                    }
                    .alerts-card-subtitle {
                        font-size: 0.85rem;
                        color: #7f8c8d;
                        margin-top: 4px;
                        margin-bottom: 0;
                    }

                    /* 탭 네비게이션 */
                    .alerts-tabs {
                        display: flex;
                        gap: 8px;
                        margin-bottom: 20px;
                        overflow-x: auto;
                        padding-bottom: 6px;
                    }
                    .alerts-tab-btn {
                        background: #f1f2f6;
                        border: 1px solid #e9ecef;
                        padding: 8px 16px;
                        border-radius: 30px;
                        font-weight: 700;
                        font-size: 0.85rem;
                        color: #57606f;
                        white-space: nowrap;
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        transition: all 0.2s ease;
                        cursor: pointer;
                    }
                    .alerts-tab-btn:hover {
                        background: #dfe4ea;
                        color: #2f3542;
                    }
                    .alerts-tab-btn.active {
                        background: #1e3c72;
                        border-color: #1e3c72;
                        color: white;
                        box-shadow: 0 4px 12px rgba(30, 60, 114, 0.18);
                    }

                    /* 특보 결과 상세 영역 */
                    .tab-result-content {
                        background: transparent;
                        border: none;
                        padding: 0;
                        min-height: 180px;
                    }
                    .alert-item-container {
                        background: white;
                        border: 1px solid #e9ecef;
                        border-radius: 16px;
                        padding: 20px;
                        box-shadow: 0 4px 15px rgba(0,0,0,0.02);
                        margin-bottom: 20px;
                    }
                    .alert-section-box {
                        background: transparent;
                        border-left: 3px solid #7f8c8d;
                        padding: 4px 0 4px 12px;
                        margin-bottom: 16px;
                        border-radius: 0;
                    }
                    .alert-section-box.active-warnings {
                        border-left-color: #e74c3c;
                        background: transparent;
                    }
                    .alert-section-box.detail-regions {
                        border-left-color: #3498db;
                        background: transparent;
                    }
                    .alert-section-box.execution-times {
                        border-left-color: #95a5a6;
                        background: transparent;
                    }
                    .alert-section-box.preliminary-warnings {
                        border-left-color: #f39c12;
                        background: transparent;
                    }
                    .alert-section-box.action-guidelines {
                        border-left-color: #2ecc71;
                        background: transparent;
                    }
                    .section-title {
                        font-size: 0.88rem;
                        margin-bottom: 6px;
                    }
                    .section-content {
                        font-size: 0.85rem;
                        line-height: 1.5;
                    }
                    .pre-wrap {
                        white-space: pre-wrap;
                    }

                    /* 태풍 레이아웃 */
                    .typhoon-grid-layout {
                        display: flex;
                        flex-direction: column;
                        gap: 24px;
                    }
                    .typhoon-specs-table {
                        font-size: 0.9rem;
                        margin-bottom: 12px;
                    }
                    .typhoon-specs-table th {
                        background-color: #f8f9fa;
                        width: 25%;
                        font-weight: 700;
                    }
                    .typhoon-map-img {
                        max-width: 100%;
                        max-height: 600px;
                        object-fit: contain;
                    }

                    /* 애니메이션 날씨 아이콘 CSS */
                    .sun-spin {
                        transform-origin: 0px 0px;
                        animation: spin-clockwise 10s linear infinite;
                    }
                    @keyframes spin-clockwise {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    .sun-core {
                        transform-origin: 0px 0px;
                        animation: core-pulse 1.8s ease-in-out infinite alternate;
                    }
                    @keyframes core-pulse {
                        0% { transform: scale(0.9); }
                        100% { transform: scale(1.1); }
                    }
                    .sun-ray {
                        animation: ray-pulse 1.8s ease-in-out infinite alternate;
                    }
                    @keyframes ray-pulse {
                        0% { opacity: 0.3; }
                        100% { opacity: 1; }
                    }
                    .sun-spin-slow {
                        transform-origin: 0px 0px;
                        animation: spin-clockwise 20s linear infinite;
                    }
                    .cloud-drift {
                        animation: cloud-float 3.5s ease-in-out infinite;
                    }
                    @keyframes cloud-float {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-4px); }
                    }
                    .cloud-drift-front {
                        animation: cloud-float-front 4.5s ease-in-out infinite;
                    }
                    @keyframes cloud-float-front {
                        0%, 100% { transform: translate(0, 0); }
                        50% { transform: translate(-3px, -2px); }
                    }
                    .cloud-drift-back {
                        animation: cloud-float-back 5.5s ease-in-out infinite;
                    }
                    @keyframes cloud-float-back {
                        0%, 100% { transform: translate(0, 0); }
                        50% { transform: translate(3px, -1px); }
                    }
                    .rain-drop {
                        animation: rain-fall 1.2s linear infinite;
                    }
                    .drop-1 { animation-delay: 0s; }
                    .drop-2 { animation-delay: 0.4s; }
                    .drop-3 { animation-delay: 0.8s; }
                    @keyframes rain-fall {
                        0% { transform: translate(4px, -10px); opacity: 0; }
                        30% { opacity: 1; }
                        100% { transform: translate(-4px, 10px); opacity: 0; }
                    }
                    .wind-line {
                        animation: wind-blow 1.8s ease-in-out infinite;
                    }
                    .line-1 { animation-delay: 0s; }
                    .line-2 { animation-delay: 0.5s; }
                    .line-3 { animation-delay: 1s; }
                    @keyframes wind-blow {
                        0% { opacity: 0; transform: translateX(-6px); }
                        50% { opacity: 1; }
                        100% { opacity: 0; transform: translateX(10px); }
                    }
                    .lightning-bolt {
                        transform-origin: center;
                        animation: lightning-flash 2.2s ease-in-out infinite;
                    }
                    @keyframes lightning-flash {
                        0%, 80%, 90%, 95%, 100% { opacity: 0; transform: scale(0); }
                        85%, 92% { opacity: 1; transform: scale(1.2) translateY(1px); }
                    }
                    .humidity-drop {
                        transform-origin: 32px 42px;
                        animation: drop-pulse 1.8s ease-in-out infinite;
                    }
                    @keyframes drop-pulse {
                        0%, 100% { transform: scale(1) translateY(0); }
                        50% { transform: scale(1.2, 0.9) translateY(1.5px); }
                    }
                    .pulse-opacity {
                        animation: pulse-op 1s infinite alternate;
                    }
                    @keyframes pulse-op {
                        0% { opacity: 0.3; }
                        100% { opacity: 1; }
                    }

                    @media (max-width: 575.98px) {
                        .weather-dashboard {
                            padding: 1rem 0.5rem;
                        }
                        .weather-header {
                            padding: 1.5rem 1rem;
                            border-radius: 16px;
                        }
                        .weather-card {
                            padding: 16px;
                            border-radius: 16px;
                            min-height: auto;
                        }
                        .alerts-card {
                            padding: 16px;
                            border-radius: 16px;
                        }
                        .alert-item-container {
                            padding: 14px;
                            border-radius: 12px;
                            margin-bottom: 16px;
                        }
                        .alert-section-box {
                            padding-left: 10px;
                            margin-bottom: 12px;
                        }
                    }
                `}
            </style>

            <Container className="weather-dashboard">
                {/* 상단 기상 대시보드 헤더 */}
                <div className="weather-header">
                    <h1 className="weather-header-title">🌦️ 통합 기상정보 대시보드</h1>
                    <p className="weather-header-subtitle">
                        실시간 기상 상황, 미세먼지 예보 흐름 및 대한민국 기상청 특보 정보를 원스톱으로 조회합니다.
                    </p>
                    <div className="weather-header-meta">
                        <div className="update-badge">
                            ⏱️ 최종 확인 시각: {formatDate(new Date())}
                        </div>
                        <button
                            className="btn-refresh"
                            onClick={handleRefreshAll}
                            disabled={refreshing || weatherLoading || dustLoading}
                        >
                            <span className={refreshing ? "spin-icon" : ""}>🔄</span>
                            {refreshing ? "새로고침 중..." : "전체 동기화"}
                        </button>
                    </div>
                </div>

                {/* 대시보드 탑 그리드 (현재 날씨 & 미세먼지) */}
                <div className="weather-top-grid">
                    {/* 실시간 날씨 현황 */}
                    <div className="weather-card">
                        <div className="weather-card-header">
                            <h3 className="weather-card-title">☀️ 실시간 기상현황</h3>
                        </div>

                        {weatherLoading ? (
                            <div className="text-center my-auto">
                                <Spinner animation="border" variant="primary" className="mb-2" />
                                <p className="text-muted small">기상 관측 서버에서 정보를 가져오고 있습니다...</p>
                            </div>
                        ) : weatherError ? (
                            <div className="text-center my-auto text-danger">
                                <p className="fw-bold">기상 정보를 가져오지 못했습니다.</p>
                                <button className="btn btn-sm btn-outline-danger px-3 rounded-pill" onClick={fetchCurrentWeather}>
                                    다시 시도
                                </button>
                            </div>
                        ) : currentWeather ? (
                            <div className="weather-current-detail">
                                {(() => {
                                    const { imageUrl, cleanContent } = parsePostContent(currentWeather.json.content);
                                    const extra = currentWeather.json.extra_data;

                                    return (
                                        <>
                                            {/* 날씨 포스트 이미지 배너 */}
                                            <div className="weather-card-img-wrapper mb-3" style={{ position: 'relative', height: '180px', borderRadius: '16px', overflow: 'hidden' }}>
                                                <img
                                                    src={imageUrl}
                                                    alt="날씨 현황 정보"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    onError={(e) => {
                                                        e.target.src = DEFAULT_WEATHER_IMG;
                                                    }}
                                                />
                                            </div>

                                            {/* 날씨 요약 띠지 */}
                                            <div className="weather-main-status">
                                                <div className="weather-status-left">
                                                    <span className="weather-status-label">오늘 날씨</span>
                                                    <span className="weather-status-name">
                                                        {extra?.weather_condition && getWeatherIcon(extra.weather_condition)}
                                                        {extra?.weather_condition ? getWeatherName(extra.weather_condition) : "관측 중"}
                                                    </span>
                                                </div>
                                                <div className="weather-main-temp">
                                                    <span className="weather-temp-label">기온</span>
                                                    <span className="weather-temp-range">
                                                        {extra?.min_temp && <span className="weather-temp-low">{extra.min_temp}</span>}
                                                        {extra?.min_temp && extra?.max_temp && <span className="mx-1" style={{ color: '#dfe6e9' }}>/</span>}
                                                        {extra?.max_temp && <span className="weather-temp-high">{extra.max_temp}</span>}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* 습도 및 관측 정보 */}
                                            <div className="weather-stats-strip">
                                                <div className="weather-stat-box">
                                                    <AnimatedHumidity />
                                                    <div className="weather-stat-info">
                                                        <span className="weather-stat-label">습도</span>
                                                        <span className="weather-stat-value">{extra?.humidity || "관측 불가"}</span>
                                                    </div>
                                                </div>
                                                <div className="weather-stat-box">
                                                    <span style={{ fontSize: '1.4rem' }}>📅</span>
                                                    <div className="weather-stat-info">
                                                        <span className="weather-stat-label">관측 기준일</span>
                                                        <span className="weather-stat-value">{formatDate(currentWeather.json.created_at)}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 날씨 포스팅 텍스트 */}
                                            <div className="weather-post-wrapper">
                                                <h4 className="weather-post-title">{currentWeather.json.title}</h4>
                                                <div className="weather-post-date">등록일: {formatDate(currentWeather.json.created_at)}</div>
                                                <div className="weather-post-body">
                                                    <Viewer initialValue={cleanContent} />
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        ) : (
                            <div className="text-center my-auto text-muted">
                                <p className="small">현재 등록된 기상 관측 리포트가 없습니다.</p>
                            </div>
                        )}
                    </div>

                    {/* 실시간 미세먼지 예보 */}
                    <div className="weather-card">
                        <div className="weather-card-header">
                            <h3 className="weather-card-title">😷 실시간 미세먼지 예보</h3>
                        </div>

                        {dustLoading ? (
                            <div className="text-center my-auto">
                                <Spinner animation="border" variant="secondary" className="mb-2" />
                                <p className="text-muted small">미세먼지 예측 모델을 가동하고 있습니다...</p>
                            </div>
                        ) : dustError ? (
                            <div className="text-center my-auto text-danger">
                                <p className="fw-bold">미세먼지 예보 정보를 가져오지 못했습니다.</p>
                                <button className="btn btn-sm btn-outline-danger px-3 rounded-pill" onClick={fetchFineDust}>
                                    다시 시도
                                </button>
                            </div>
                        ) : pm10State ? (
                            <div className="finedust-detail">
                                <div className="finedust-forecast-area">
                                    <div className="finedust-map-box">
                                        <div className="finedust-map-img-container">
                                            {imageUrls.length > 0 && (
                                                <img
                                                    src={imageUrls[currentIndex]}
                                                    alt="미세먼지 예측지도"
                                                    className="finedust-map-img"
                                                />
                                            )}
                                        </div>
                                        <div className="finedust-map-dots">
                                            {imageUrls.map((_, i) => (
                                                <span
                                                    key={i}
                                                    className={`finedust-dot ${currentIndex === i ? 'active' : ''}`}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    <div className="finedust-text-info">
                                        <div className="finedust-info-section">
                                            <div className="finedust-info-section-title">⏱️ 예측 모델 흐름 (PM10)</div>
                                            <p className="finedust-info-section-text" style={{ color: '#747d8c', fontSize: '0.78rem' }}>
                                                ※ 0.5초 간격으로 시간별 미세먼지 분포 흐름이 자동 애니메이션 상영됩니다.
                                            </p>
                                        </div>

                                        <div className="finedust-info-section">
                                            <div className="finedust-info-section-title">⚠️ 미세먼지 발생원인</div>
                                            <p className="finedust-info-section-text">{pm10State.informCause}</p>
                                        </div>

                                        <div className="finedust-info-section">
                                            <div className="finedust-info-section-title">📋 미세먼지 종합전망</div>
                                            <p className="finedust-info-section-text">{pm10State.informOverall}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="finedust-grades-panel">
                                    <h4 className="finedust-grades-title">🗺️ 전국 지역별 예보 등급</h4>
                                    <div className="finedust-grades-grid">
                                        {parseGrades(pm10State.informGrade).map((item, idx) => {
                                            const colors = getGradeColor(item.grade);
                                            return (
                                                <span
                                                    key={idx}
                                                    className="finedust-grade-chip animate-chip"
                                                    style={{
                                                        backgroundColor: colors.bg,
                                                        color: colors.text,
                                                        borderColor: colors.border,
                                                        borderWidth: '1px',
                                                        display: 'inline-block'
                                                    }}
                                                >
                                                    {item.region} : {item.grade}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center my-auto text-muted">
                                <p className="small">현재 조회 가능한 미세먼지 예보가 없습니다.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* 대시보드 하단 (기상청 실시간 특보 & 속보 탭 영역) */}
                <div className="alerts-card">
                    <div className="alerts-card-header">
                        <h3 className="alerts-card-title">🚨 기상청 실시간 특보 및 속보 아카이브</h3>
                        <p className="alerts-card-subtitle">
                            기상청 공공데이터를 기반으로 실시간 발표되는 특보, 예비특보, 기상속보, 기상정보 및 진행 중인 태풍 현황입니다.
                        </p>
                    </div>

                    {/* 탭 네비게이션 */}
                    <div className="alerts-tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                className={`alerts-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <span className="me-1" style={{ fontSize: '1.1rem', verticalAlign: 'middle' }}>{tab.icon}</span>
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>

                    {/* 탭 콘텐츠 영역 */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            {renderTabContent(activeTab)}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </Container>
        </>
    );
};
